/**
 * Attaches unified scroll-drag behaviour to a Pixi hit area:
 *   - mouse wheel
 *   - click-and-drag on the content (mouse)
 *   - touch-and-drag (pointer events)
 *
 * Vertical-only. The caller owns the mask, the content container, and the
 * scroll position — this utility just translates input into `onDelta(dy)`
 * calls (positive dy = scroll content upward / reveal content below).
 *
 * Conflict handling:
 *   - 5px movement threshold distinguishes taps from drags. Taps pass through
 *     to children; drags claim the pointer and suppress the subsequent tap via
 *     `wasDragging()`, which child pointertap handlers can check.
 *   - `cancelDrag()` lets a competing gesture (e.g. inventory long-press)
 *     abort an in-flight scroll drag.
 */
import type { Container, FederatedPointerEvent } from 'pixi.js';

export interface ScrollDragOptions {
  /** Hit target that receives pointer/wheel events. Must have eventMode='static'. */
  hitArea: Container;
  /** Called with pixel delta in scroll space. Positive = scroll down (content up). */
  onDelta: (dy: number) => void;
  /** Drag distance in px before a press becomes a drag. Default 5. */
  dragThreshold?: number;
  /** Optional: predicate to reject pointerdown (e.g. over a no-drag zone). */
  shouldStartDrag?: (e: FederatedPointerEvent) => boolean;
}

export interface ScrollDragHandle {
  /** True while the user is actively dragging past the threshold. */
  isDragging: () => boolean;
  /**
   * True if the most recent pointer gesture crossed the drag threshold.
   * Child pointertap handlers should check this and early-return to prevent
   * the gesture from registering as a click. Cleared on next pointerdown.
   */
  wasDragging: () => boolean;
  /** Forcefully end any in-flight drag (e.g. when a long-press takes over). */
  cancelDrag: () => void;
  destroy: () => void;
}

export function attachScrollDrag(opts: ScrollDragOptions): ScrollDragHandle {
  const { hitArea, onDelta } = opts;
  const threshold = opts.dragThreshold ?? 5;

  hitArea.cursor = 'grab';

  let pressed = false;
  let dragging = false;
  let didDrag = false;
  let startY = 0;
  let lastY = 0;

  const onWheel = (e: WheelEvent) => {
    onDelta(e.deltaY);
  };

  const onDown = (e: FederatedPointerEvent) => {
    if (opts.shouldStartDrag && !opts.shouldStartDrag(e)) return;
    pressed = true;
    dragging = false;
    didDrag = false;
    startY = e.global.y;
    lastY = e.global.y;
  };

  const onMove = (e: FederatedPointerEvent) => {
    if (!pressed) return;
    const curY = e.global.y;
    if (!dragging) {
      if (Math.abs(curY - startY) < threshold) return;
      dragging = true;
      didDrag = true;
      hitArea.cursor = 'grabbing';
    }
    const dy = lastY - curY; // pointer down ⇒ scroll up
    lastY = curY;
    if (dy !== 0) onDelta(dy);
  };

  const endDrag = () => {
    if (!pressed) return;
    pressed = false;
    dragging = false;
    hitArea.cursor = 'grab';
  };

  hitArea.eventMode = 'static';
  hitArea.on('wheel', onWheel);
  hitArea.on('pointerdown', onDown);
  hitArea.on('globalpointermove', onMove);
  hitArea.on('pointerup', endDrag);
  hitArea.on('pointerupoutside', endDrag);
  hitArea.on('pointercancel', endDrag);

  return {
    isDragging: () => dragging,
    wasDragging: () => didDrag,
    cancelDrag: () => {
      didDrag = false;
      endDrag();
    },
    destroy: () => {
      hitArea.off('wheel', onWheel);
      hitArea.off('pointerdown', onDown);
      hitArea.off('globalpointermove', onMove);
      hitArea.off('pointerup', endDrag);
      hitArea.off('pointerupoutside', endDrag);
      hitArea.off('pointercancel', endDrag);
    },
  };
}
